import React, { useEffect, useRef, useState } from 'react';
import FileTree from './Components/FileTree/FileTree';
import styles from './FileExplorer.module.scss';
import { FileNode } from '../../DataRoom';
import { Button } from '../../../../common/_custom';
import { downloadDarkGray, downloadIconDataRoomHovered, uploadIcon, WHITE_DROPDOWN_CARET } from '../../../../assets/hostedassets';
import { isEmpty, isLenderType, resolveRegisteredCompanyName } from '../../../../utils/utils';
import { useOutsideClick } from '../../../../utils/constants/hooks';
import EmptyBank from '../../../Dashboard/DashboardModules/BankDataTable/components/EmptyBank/EmptyBank';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import useFetchDataRoomFiles, {
    getSelectedFileNodes,
    buildDownloadStructure,
    getAllFileIds,
    getAllFolderIds,
    findMasterFolderForNode,
} from '../../useFetchDataRoomFiles';
import CategorySelector from '../../../../common/CategorySelector/CategorySelector';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { useDrawerUrlFlag, DATA_ROOM_BULK_ACTION_PARAM } from '../../../../common/hooks/useDrawerUrlFlag';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { USER_TYPE } from '../../../../utils/constants/user';
import BulkActionBar from '../BulkActionBar/BulkActionBar';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_DATAROOM_BULK_ACTION_COMPLETED, AICA_DATAROOM_MAGIC_UPLOAD } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { getSubdomain } from '../../../../utils/utils';
import { MAGIC_UPLOAD_TYPE } from '../../../../store/investor/constants';
import useMagicUploadLogic from '../../../../common/MagicUpload/useMagicUploadLogic';
import DownloadForOfflineIcon from '../../../../assets/Svg/DownloadForOfflineIcon';
import { useFeatureVisibility, useVisibilityConfig } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { useAddToReportsVisible } from '../../../../common/Reports/useAddToReportsVisible';
import { createMagicSignRecord } from '../../../../common/MagicSign/MagicSignService';
import { MAGIC_SIGN_ALLOWED_EXTENSIONS, MagicSignSource } from '../../../../common/MagicSign/constants';
import { MAGIC_SIGN } from '../../../../common/MagicUpload/Utils/MagicUploadConstants';

// Uploaded and MCA Records both use the same green count pill (see CategorySelector's pillVariant).
const GREEN_COUNT_PILL = 'green' as const;

interface FileExplorerProps {
    data: FileNode[];
    activeFilecount: number;
    archivedFilecount: number;
    selectedCompany: any;
    exportData: Function;
    fetchFiles: Function;
    setShowDataRoomModal: (value: {
        showModal: boolean;
        docId?: number;
        currentDocType?: string;
        selectedDocIds?: number[];
        onMoveSuccess?: () => void;
    }) => void;
    newFilePresent?: boolean;
    mcaRecordsFileCount?: number;
    // True while the current tab's files are being (re)fetched — drives the in-area loading state.
    isFetchingFiles?: boolean;
    // isRecurCompany: boolean;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
    data,
    activeFilecount,
    archivedFilecount,
    selectedCompany,
    exportData,
    fetchFiles,
    setShowDataRoomModal,
    newFilePresent,
    mcaRecordsFileCount,
    isFetchingFiles,
    // isRecurCompany,
}) => {
    const { downloadTheFolder, downloading, downloadingNew, handleDeleteFile, handleArchiveFileBulk, handleUnarchiveFileBulk } =
        useFetchDataRoomFiles(fetchFiles);
    const [searchParams, setSearchParams] = useSearchParams();
    const [fileViewType, setFileViewType] = useState('');
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [isBulkActionBarVisible, setIsBulkActionBarVisible] = useState(false);
    // Top-right "Download" split-button dropdown (All Files / Only New Files)
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(downloadMenuRef, () => setShowDownloadMenu(false));
    const user = useAppSelector((state) => state.user);
    const isLender = isLenderType();
    const isPartner = user?.userData?.accountType === USER_TYPE.PARTNER;
    const isCompany = user?.userData?.accountType === USER_TYPE.COMPANY;
    const isLenderOrPartner = isLender || isPartner;
    const isArchiveView = fileViewType === 'archived';
    // MCA records are read-only sweep documents: no archiving from the UI, so they never leave the tab.
    const isMcaRecordsView = fileViewType === 'mcaRecords';
    const { dataRoomDownloadVisibility, magicSignVisibility, showSignedCopyVisibility } = useFeatureVisibility({
        dataRoomDownloadVisibility: VISIBILITY_KEYS.DATA_ROOM.DOWNLOAD,
        magicSignVisibility: VISIBILITY_KEYS.MAGIC_SIGN.ENABLED,
        showSignedCopyVisibility: VISIBILITY_KEYS.MAGIC_SIGN.SHOW_SIGNED_COPY,
    });
    const addToReportsVisible = useAddToReportsVisible();
    // Fail-closed: the tab shows only on orgs whose visibility config sets the key.
    const { isExplicitlyEnabled } = useVisibilityConfig();

    const { changeDrawerContent, openCompanyMagicUpload } = useMagicUploadLogic();

    const handleSendForSigning = async (docId: string, fileName: string) => {
        if (!magicSignVisibility.show) return;
        try {
            // DocStore row already exists (registered when the file was uploaded to the Data Room).
            // Call /magic/sign/create directly — no re-registration needed.
            // docId here IS the mnemosyneDocId (see FileNode.docId — same id space).
            await createMagicSignRecord({
                mnemosyneDocId: docId,
                fileName,
                source: MagicSignSource.DATA_ROOM,
                orgId: selectedCompany?.assesseeOrgId,
            });
            toast.success('Document added to Magic Sign.');
            fetchFiles();
            changeDrawerContent(MAGIC_SIGN);
        } catch (error) {
            console.error('[FileExplorer] Failed to send document for signing', error);
            toast.error(error instanceof Error && error.message ? error.message : 'Failed to send document for signing.');
        }
    };

    const VIEW_TYPE = [
        {
            id: 'active',
            label: 'Uploaded',
            count: activeFilecount ?? null,
            pillVariant: GREEN_COUNT_PILL,
        },
        ...(isExplicitlyEnabled(VISIBILITY_KEYS.DATA_ROOM.MCA_RECORDS)
            ? [
                  {
                      id: 'mcaRecords',
                      label: 'MCA Records',
                      count: mcaRecordsFileCount,
                      pillVariant: GREEN_COUNT_PILL,
                  },
              ]
            : []),
        {
            id: 'archived',
            label: 'Archived',
            count: archivedFilecount ?? null,
            pillVariant: 'grey' as const,
        },
    ];
    useEffect(() => {
        const currentViewType = searchParams.get('fileViewType');
        if (currentViewType) {
            setFileViewType(currentViewType);
            return;
        }
        const defaultViewType = 'active';
        setFileViewType(defaultViewType);
        // Seeding the default is the only write this effect owes the URL, and it goes in as a
        // replace — writing back on every searchParams change left a history entry per selection
        // toggle, so Back looked dead.
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('fileViewType', defaultViewType);
        setSearchParams(nextParams, { replace: true });
    }, [searchParams]);

    const changeSelectedCategory = (categoryId) => {
        if (categoryId === fileViewType) return;
        setFileViewType(categoryId);
        updateQueryParams(searchParams, setSearchParams, { fileViewType: categoryId });
        setSelectedItems(new Set()); // Clear selection when switching tabs
        setSelectedFolders(new Set()); // Clear folder selection when switching tabs
        // Folder open/close states are reset automatically via component keys
        // The in-area loading state is driven by isFetchingFiles (the actual per-tab fetch), not a timer.
    };

    // Manage bulk action bar animation visibility
    useEffect(() => {
        if (selectedItems.size > 0) {
            setIsBulkActionBarVisible(true);
            // Top-right Download becomes disabled while selecting — close its dropdown too
            setShowDownloadMenu(false);
        } else {
            setIsBulkActionBarVisible(false);
        }
    }, [selectedItems.size]);

    // Hide the floating Ask AICA icon while files/folders are selected here — it visually
    // overlaps the bulk-action footer bar (RECUR-28029). AskAicaV2 reads this same param.
    useDrawerUrlFlag(selectedItems.size > 0 || selectedFolders.size > 0, DATA_ROOM_BULK_ACTION_PARAM);

    // Helper function to recursively collect all docIds from selected items
    const getAllDocIds = (items: FileNode[]): string[] => {
        let docIds: string[] = [];
        items.forEach((item) => {
            if (item.docId) {
                docIds.push(String(item.docId));
            }
            if (item.innerFiles && item.innerFiles.length > 0) {
                docIds = [...docIds, ...getAllDocIds(item.innerFiles)];
            }
        });
        return docIds;
    };

    // Neither collection is unique — folders are keyed by name, and getAllFileIds walks a tree
    // merged out of AICA + Olympus docs, so one docId can sit in two branches. The selection Sets
    // collapse those repeats, so this has to compare deduped against deduped or it never matches.
    const allFileIds = new Set(getAllFileIds(data));
    const allFolderIds = new Set(getAllFolderIds(data));
    const isEverythingSelected = !isEmpty(data) && selectedItems.size === allFileIds.size && selectedFolders.size === allFolderIds.size;

    // Handle select all (both files and folders)
    const handleSelectAll = () => {
        if (isEverythingSelected) {
            setSelectedItems(new Set());
            setSelectedFolders(new Set());
        } else {
            setSelectedItems(new Set(allFileIds));
            setSelectedFolders(new Set(allFolderIds));
        }
    };

    // Get all file IDs within a folder (recursively)
    const getAllFileIdsInFolder = (folder: FileNode): string[] => {
        let fileIds: string[] = [];
        if (folder.innerFiles) {
            folder.innerFiles.forEach((item) => {
                if (item.type === 'file' && item.docId) {
                    fileIds.push(String(item.docId));
                } else if (item.type === 'folder' && item.innerFiles) {
                    fileIds = [...fileIds, ...getAllFileIdsInFolder(item)];
                }
            });
        }
        return fileIds;
    };

    // Get all folder IDs within a folder (recursively)
    const getAllFolderIdsInFolder = (folder: FileNode): string[] => {
        let folderIds: string[] = [];
        if (folder.innerFiles) {
            folder.innerFiles.forEach((item) => {
                if (item.type === 'folder') {
                    folderIds.push(item.name);
                    folderIds = [...folderIds, ...getAllFolderIdsInFolder(item)];
                }
            });
        }
        return folderIds;
    };

    // Find node by ID
    const findNodeById = (nodes: FileNode[], id: string): FileNode | null => {
        for (const node of nodes) {
            const nodeId = node.docId ? String(node.docId) : node.name;
            if (nodeId === id) return node;
            if (node.innerFiles) {
                const found = findNodeById(node.innerFiles, id);
                if (found) return found;
            }
        }
        return null;
    };

    // Find all parent folder IDs of a given item. Returns null when targetId isn't found
    // anywhere in this branch — previously this returned the accumulated (but bogus) parents
    // array instead, so deselecting any top-level item falsely inherited the first top-level
    // folder as a "parent" and got it deselected too (RECUR-28029).
    const findParentFoldersInBranch = (nodes: FileNode[], targetId: string): string[] | null => {
        for (const node of nodes) {
            if (node.type === 'folder' && node.innerFiles) {
                const directChild = node.innerFiles.find((child) => {
                    const childId = child.docId ? String(child.docId) : child.name;
                    return childId === targetId;
                });

                if (directChild) {
                    return [node.name];
                }

                const foundInNested = findParentFoldersInBranch(node.innerFiles, targetId);
                if (foundInNested !== null) {
                    return [node.name, ...foundInNested];
                }
            }
        }
        return null;
    };
    const findParentFolders = (nodes: FileNode[], targetId: string): string[] => {
        return findParentFoldersInBranch(nodes, targetId) ?? [];
    };

    // Check if all files in folder are selected
    const areAllFilesInFolderSelected = (folder: FileNode, selected: Set<string>): boolean => {
        const fileIds = getAllFileIdsInFolder(folder);
        return fileIds.length > 0 && fileIds.every((id) => selected.has(id));
    };

    // Handle individual item selection with folder/file logic
    const handleItemSelect = (itemId: string) => {
        const node = findNodeById(data, itemId);
        if (!node) return;

        const newSelected = new Set(selectedItems);
        const newSelectedFolders = new Set(selectedFolders);

        if (node.type === 'folder') {
            const fileIds = getAllFileIdsInFolder(node);
            const folderIds = getAllFolderIdsInFolder(node);

            if (selectedFolders.has(itemId)) {
                // Deselect folder and all children
                fileIds.forEach((id) => newSelected.delete(id));
                folderIds.forEach((id) => newSelectedFolders.delete(id));
                newSelectedFolders.delete(itemId);

                // Deselect all parent folders
                const parentFolders = findParentFolders(data, itemId);
                parentFolders.forEach((parentId) => newSelectedFolders.delete(parentId));
            } else {
                // Select folder and all children
                fileIds.forEach((id) => newSelected.add(id));
                folderIds.forEach((id) => newSelectedFolders.add(id));
                newSelectedFolders.add(itemId);
            }
        } else {
            if (newSelected.has(itemId)) {
                // Deselect file
                newSelected.delete(itemId);

                // Deselect all parent folders
                const parentFolders = findParentFolders(data, itemId);
                parentFolders.forEach((parentId) => newSelectedFolders.delete(parentId));
            } else {
                // Select file
                newSelected.add(itemId);
            }
        }

        setSelectedItems(newSelected);
        setSelectedFolders(newSelectedFolders);
    };

    // Get count of only files (not folders)
    const getSelectedFileCount = (): number => {
        return selectedItems.size;
    };

    // Check if folder should be shown as selected (only if explicitly selected)
    const isFolderFullySelected = (folderId: string): boolean => {
        return selectedFolders.has(folderId);
    };

    // Bulk Actions
    const handleBulkArchive = async () => {
        setBulkActionLoading(true);
        try {
            const selectedNodes = getSelectedFileNodes(data, selectedItems);

            if (selectedNodes.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Collect all files with their respective docTypes
            const filesWithDocType = selectedNodes
                .filter((node) => node.type === 'file' && node.docId)
                .map((node) => {
                    const fileId = String(node.docId!);
                    const masterFolder = findMasterFolderForNode(data, fileId);
                    return {
                        docId: node.docId!,
                        docType: masterFolder,
                    };
                });

            if (filesWithDocType.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Make a single API call with all files
            const result = await handleArchiveFileBulk(filesWithDocType);

            if (result !== false) {
                // Track Mixpanel event
                Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                    action_type: 'archive',
                    item_count: filesWithDocType.length,
                    lead_id: selectedCompany?.assesseeOrgId,
                    user_id: user?.userData?.userId,
                });

                setSelectedItems(new Set());
                setSelectedFolders(new Set());
                fetchFiles();
            }
        } catch (error) {
            toast.error('Failed to archive items');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkMove = () => {
        const selectedNodes = getSelectedFileNodes(data, selectedItems);
        if (selectedNodes.length === 0) {
            toast.error('No items selected');
            return;
        }

        // Get all selected doc IDs (only files)
        const selectedDocIds = selectedNodes.filter((node) => node.type === 'file' && node.docId).map((node) => node.docId as number);

        // Open move modal with first selected file's docId and all selected doc IDs
        const firstFile = selectedNodes.find((node) => node.type === 'file');
        if (firstFile?.docId && selectedDocIds.length > 0) {
            setShowDataRoomModal({
                showModal: true,
                docId: firstFile.docId,
                currentDocType: firstFile.name,
                selectedDocIds: selectedDocIds,
                onMoveSuccess: () => {
                    // Track Mixpanel event
                    Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                        action_type: 'move',
                        item_count: selectedDocIds.length,
                        lead_id: selectedCompany?.assesseeOrgId,
                        user_id: user?.userData?.userId,
                    });

                    // Clear selections after successful move
                    setSelectedItems(new Set());
                    setSelectedFolders(new Set());
                },
            });
        }
    };

    const handleBulkDownload = async () => {
        setBulkActionLoading(true);
        try {
            if (selectedItems.size === 0) {
                toast.error('No items selected');
                return;
            }

            // Build structured hierarchy for download (maintains folder structure)
            const structuredData = buildDownloadStructure(data, selectedItems);

            if (structuredData.length === 0) {
                toast.error('No items to download');
                return;
            }

            await downloadTheFolder(structuredData, selectedCompany?.orgName ?? 'Data', false);

            // Track Mixpanel event
            Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                action_type: 'download',
                item_count: selectedItems.size,
                lead_id: selectedCompany?.assesseeOrgId,
                user_id: user?.userData?.userId,
            });

            setSelectedItems(new Set());
            setSelectedFolders(new Set());
        } catch (error) {
            toast.error('Failed to download items');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkMoveToActive = async () => {
        setBulkActionLoading(true);
        try {
            const selectedNodes = getSelectedFileNodes(data, selectedItems);

            if (selectedNodes.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Collect all files with their respective docTypes
            const filesWithDocType = selectedNodes
                .filter((node) => node.type === 'file' && node.docId)
                .map((node) => {
                    const fileId = String(node.docId!);
                    const masterFolder = findMasterFolderForNode(data, fileId);
                    return {
                        docId: node.docId!,
                        docType: masterFolder,
                    };
                });

            if (filesWithDocType.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Make a single API call with all files
            const result = await handleUnarchiveFileBulk(filesWithDocType);

            if (result !== false) {
                // Track Mixpanel event
                Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                    action_type: 'move_to_active',
                    item_count: filesWithDocType.length,
                    lead_id: selectedCompany?.assesseeOrgId,
                    user_id: user?.userData?.userId,
                });

                setSelectedItems(new Set());
                setSelectedFolders(new Set());
                fetchFiles();
            }
        } catch (error) {
            toast.error('Failed to move items to active');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const isDownloading = downloading || downloadingNew;
    const downloadDisabled = isEmpty(data) || selectedItems.size > 0 || dataRoomDownloadVisibility.disabled || isDownloading;
    const downloadAll = async () => {
        setShowDownloadMenu(false);
        await downloadTheFolder(data, selectedCompany?.orgName ?? 'Data', false);
    };
    const downloadNew = async () => {
        setShowDownloadMenu(false);
        await downloadTheFolder(data, selectedCompany?.orgName ?? 'Data', true);
    };

    // Shared by the header Upload button and the empty-state "Upload Data" CTA — same intent,
    // same drawer, same company scope, same tracking payload.
    const handleUploadClick = () => {
        openCompanyMagicUpload(resolveRegisteredCompanyName(selectedCompany));
        Mixpanel.track(AICA_DATAROOM_MAGIC_UPLOAD, {
            userRole: user?.userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: user?.userData?.investorOrgId,
            userId: user?.userData?.userId,
            currentUrl: window.location.href,
            accessedPlatform: user?.userData?.accountType,
            individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
            organizationId: selectedCompany?.assesseeOrgId,
        });
    };

    return (
        <div className={styles.fileExplorer}>
            <header className={styles.header}>
                <div className={styles.leftSubHeader}>
                    <h1>Dataroom</h1>
                    {!isLender && (
                        <div className={styles.selectorView}>
                            <div className={styles.separator} />
                            <div className={styles.CategorySelector}>
                                <CategorySelector
                                    categoryOptions={VIEW_TYPE}
                                    selectedCategory={fileViewType}
                                    changeSelectedTab={changeSelectedCategory}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {isPartner && <></>}

                <div className={styles.HeaderActions}>
                    {!isLenderOrPartner && (
                        <Button
                            type="button"
                            className={styles.UploadBtn}
                            icon={downloadIconDataRoomHovered}
                            onClick={handleUploadClick}
                            text="Upload"
                        />
                    )}
                    {!isPartner && dataRoomDownloadVisibility.show && (!isLender || !dataRoomDownloadVisibility.disabled) && (
                        <div
                            className={`${styles.DownloadGroup} ${downloadDisabled ? styles.DownloadGroupDisabled : ''}`}
                            ref={downloadMenuRef}
                        >
                            <Button
                                type="button"
                                className={styles.DownloadMainBtn}
                                disabled={downloadDisabled}
                                onClick={() => setShowDownloadMenu((prev) => !prev)}
                                text={isDownloading ? 'Downloading…' : 'Download'}
                            />
                            <Button
                                type="button"
                                className={styles.DownloadCaretBtn}
                                disabled={downloadDisabled}
                                aria-label="Download options"
                                onClick={() => setShowDownloadMenu((prev) => !prev)}
                                text={
                                    <img
                                        src={WHITE_DROPDOWN_CARET}
                                        alt={''}
                                        className={`${styles.DownloadCaret} ${showDownloadMenu ? styles.DownloadCaretOpen : ''}`}
                                    />
                                }
                            />
                            {showDownloadMenu && (
                                // role="group", not "menu": these are ordinary Buttons, and menu
                                // semantics would promise menuitem children plus arrow-key
                                // navigation that isn't implemented. The aria-label still names them.
                                <div className={styles.DownloadPopover} role="group" aria-label="Download options">
                                    <Button
                                        type="button"
                                        className={styles.DownloadPopoverItemBtn}
                                        onClick={downloadAll}
                                        text="All Files"
                                    />
                                    <Button
                                        type="button"
                                        className={styles.DownloadPopoverItemBtn}
                                        disabled={!newFilePresent}
                                        onClick={downloadNew}
                                        text="Only New Files"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>
            <div className={styles.fileList}>
                <div className={isPartner ? styles.fileTreeHeaderLenderView : styles.fileTreeHeader}>
                    <div className={styles.fileTreeHeaderItem}>
                        {!isPartner && !isEmpty(data) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '0.5rem' }}>
                                <span style={{ width: '0.75rem', display: 'inline-block' }}></span>
                                <input
                                    type="checkbox"
                                    data-testid="select-all-checkbox"
                                    checked={isEverythingSelected}
                                    onChange={handleSelectAll}
                                    className={styles.checkbox}
                                />
                            </div>
                        )}
                        Data
                    </div>
                    <div className={styles.fileTreeHeaderItem}>Status</div>
                    <div className={styles.fileTreeHeaderItem}>Data From</div>
                    <div className={styles.fileTreeHeaderItem}>Data Till</div>
                    <div className={styles.fileTreeHeaderItem} style={{ paddingLeft: '2px' }}>
                        Last Updated
                    </div>
                    <div className={styles.fileTreeHeaderItem} />
                </div>
                <div className={styles.FileTreeContainer}>
                    {isFetchingFiles ? (
                        <div
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '200px' }}
                        >
                            <LoadingSpinner color={'#4D4D4D'} height={'40px'} />
                        </div>
                    ) : isEmpty(data) ? (
                        <EmptyBank
                            text={'No Data Available'}
                            subText={'Upload data to submit this case.'}
                            buttonComponent={
                                // Same gate as the header Upload button above. A UI-surface choice, not an
                                // authorization boundary: lenders and partners already get a company-scoped
                                // Magic Upload in the company header through this same opener.
                                !isLenderOrPartner && (
                                    <Button
                                        disabled={false}
                                        text={
                                            <div className={styles.uploadButton}>
                                                <DownloadForOfflineIcon fill={'var(--bg-color-white)'} width={16} height={16} />
                                                Upload Data
                                            </div>
                                        }
                                        loading={false}
                                        loadingText={'Uploading'}
                                        onClick={handleUploadClick}
                                        variant={'purple'}
                                    />
                                )
                            }
                        />
                    ) : (
                        <div style={{ height: '100%' }}>
                            <FileTree
                                data={data}
                                index={0}
                                fetchFiles={fetchFiles}
                                setShowDataRoomModal={setShowDataRoomModal}
                                selectedItems={selectedItems}
                                onItemSelect={handleItemSelect}
                                showCheckboxes={!isPartner}
                                isFolderFullySelected={isFolderFullySelected}
                                fileViewType={fileViewType}
                                dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                                addToReportsVisible={addToReportsVisible}
                                onSendForSigning={magicSignVisibility.show ? handleSendForSigning : undefined}
                                signableExtensions={magicSignVisibility.show ? MAGIC_SIGN_ALLOWED_EXTENSIONS : []}
                                showSignedCopyVisibility={showSignedCopyVisibility}
                                // isRecurCompany={isRecurCompany}
                            />
                        </div>
                    )}
                </div>
            </div>

            {selectedItems.size > 0 && (
                <BulkActionBar
                    dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                    selectedCount={getSelectedFileCount()}
                    onArchive={handleBulkArchive}
                    onMove={handleBulkMove}
                    onDownload={handleBulkDownload}
                    onMoveToActive={handleBulkMoveToActive}
                    onClearSelection={() => {
                        setSelectedItems(new Set());
                        setSelectedFolders(new Set());
                    }}
                    isMoveDisabled={selectedFolders.size > 0}
                    showArchiveButton={!isLenderOrPartner && !isMcaRecordsView}
                    showMoveButton={!isLenderOrPartner}
                    isArchived={isArchiveView}
                    loading={bulkActionLoading}
                    isVisible={isBulkActionBarVisible}
                />
            )}
        </div>
    );
};

export default FileExplorer;
